/**
 * `executeTurn` — one deterministic turn, atomically (architecture.md §7.2,
 * all eight steps: step 6 became the real scheduler tick in R9, step 6c the
 * agent step in D234 — the foes' strikes, after the player's — step 7 the
 * real trigger evaluation in R11, and step 7b the end-of-turn tick in D188 —
 * the deadlines that yield to the act that beats them; step 4 is D68's
 * deliberate absence). Steps 1–3b — the proposal narrowed, re-validated,
 * gated and handled — are here; steps 4–8 — time, the watchers, the agents
 * and the commit — are `complete-turn.ts`, split out by D235 so that an
 * answer to an actor's question, which is a turn with no command, runs the
 * same tail.
 *
 * @remarks
 * The engine's low-level entry point: a legal `ParsedCommand` in, ONE
 * result-typed outcome out — either the atomic `TurnExecutionResult`
 * (executed) or a typed `ActionRejection` (refused). No partial mutations
 * escape mid-pipeline (the store commits the result atomically, §8), and
 * nothing here reads a clock or draws unseeded randomness: same bundle +
 * same state + same command ⇒ same outcome, byte for byte.
 *
 * Step 2's re-validation is deliberate even though refs originate from a
 * catalog: the parser's proposal may be a turn stale (R6's clarification
 * flow) or hand-built (tests, tooling), so every item ref is checked against
 * THIS turn's catalog before anything acts on it.
 *
 * Commitment semantics: executed turns append their entries to
 * `session.transcript` and advance `turnCount` (v0 policy: every executed
 * action consumes time; rejections never do). `commandHistory` — the
 * VERBATIM raw input — is `processPlayerInput`'s to record in R6:
 * `executeTurn` only ever sees the parsed clause. Rejection narratives are
 * returned UNSTAMPED for the same reason: the rejection arm's transcript
 * commitment belongs to the outcome's consumer (architecture.md §3.3/§8).
 *
 * @packageDocumentation
 */

import type { ItemId, RuntimeState } from '@orpheus/content-schema';
import type { ParsedCommand } from '@orpheus/parser-engine';

import type { ActionRejection, RuntimeAction } from '../actions/runtime-action.js';
import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { narrowParsedCommand } from '../actions/narrow-parsed-command.js';
import { commandInHandOf } from '../conditions/command-in-hand.js';
import { speakSlot } from '../defaults/default-responses.js';
import { resolveNarrativeLines } from '../narrative/conditional-text.js';
import { rejected } from './action-handler-result.js';
import {
	type ActionHandlerResult,
	handleAsk,
	handleDoor,
	handleDrop,
	handleGo,
	handleHelp,
	handleInspect,
	handleInventory,
	handleLook,
	handleMap,
	handleSearch,
	handleSearchRoom,
	handleSwitch,
	handleTake,
	handleUse,
	handleWait,
	handleWear,
} from './action-handlers.js';
import { handleBodyAct } from './actor-handlers.js';
import { handleAttack } from './attack-handler.js';
import { findReferenceOutsideCatalog, itemIdsReferencedBy } from './catalog-revalidation.js';
import { completeTurn, type TurnExecutionResult } from './complete-turn.js';
import { addressedActorOf, rememberConversationPartner } from './conversational-partner.js';
import { findSpentRefusal, spendDurability } from './durability.js';
import { handleGive } from './give-handler.js';
import { isItemUnreachable, unreachableRefusalParts } from './reachability.js';
import { handleInspectSet } from './set-handlers.js';
import { createTurnCursor, type TranscriptCursor } from './transcript-cursor.js';
import { handleAuthoredTable } from './verb-handlers.js';

// The atomic product of a turn is defined where the commit happens (`complete-turn.ts`); every consumer still reads it from here.
export { type TurnExecutionResult } from './complete-turn.js';

/** How one turn ended — the two `executeTurn`-level arms of the §3.3 outcome union. */
export type ExecuteTurnOutcome =
	| { readonly rejection: ActionRejection; readonly type: 'action_rejected' }
	| { readonly result: TurnExecutionResult; readonly type: 'executed' };

/**
 * Execute one turn against a loaded bundle.
 *
 * @param bundle - The loaded bundle
 * @param state - The state BEFORE this turn
 * @param command - The parser's structured proposal
 * @returns The atomic result, or the typed rejection
 */
export const executeTurn = (bundle: EngineGameBundle, state: RuntimeState, command: ParsedCommand): ExecuteTurnOutcome => {
	// Step 1 — narrow the proposal; unknown intents are typed rejections.
	const narrowing = narrowParsedCommand(command, bundle, state);

	if (narrowing.type === 'rejected') {
		return { rejection: narrowing.rejection, type: 'action_rejected' };
	}

	// Step 2 — re-validate every referenced item (and, D232, every referenced
	// actor) against THIS turn's catalog.
	const staleRef = findReferenceOutsideCatalog(bundle, state, narrowing.action);

	if (staleRef !== undefined) {
		const spoken = speakSlot(bundle, state, 'entityNotVisible');

		return {
			rejection: {
				consumesTurn: false,
				// R15: resolved at creation, like every rejection (Calder's §5.2).
				narratives: resolveNarrativeLines([spoken.line], bundle, state),
				reason: { entityRef: staleRef, type: 'entity_not_visible' },
				spokenSlots: [spoken.slot],
			},
			type: 'action_rejected',
		};
	}

	/*
	 * R29 (D105; Calder's D104 §3.1) — the reach gate: "you can see this and
	 * not reach it." One interception for every ACTING verb, before any
	 * handler runs, so a verb added next year is covered without anyone
	 * remembering this. Observation is deliberately exempt (`inspect`, `ask`,
	 * a destination-only `go`) — the player can SEE the thing; that is the
	 * whole point of the barrier. Reachability walks the containment chain
	 * (an unreachable desk's drawer, and the drawer's contents, are behind
	 * the same glass), and a carried item is reached by definition.
	 */
	const unreachableItemId = findUnreachableActedItem(bundle, state, narrowing.action);

	if (unreachableItemId !== undefined) {
		const refusalParts = unreachableRefusalParts(bundle, state, unreachableItemId);
		const gateResult = rejected(bundle, state, refusalParts.reason, refusalParts.speakable);

		return { rejection: gateResult.rejection, type: 'action_rejected' };
	}

	// D152 — the spent gate, the reach gate's sibling: an act a spent thing
	// refuses is refused here, for every verb, before any handler runs.
	const spentRefusal = findSpentRefusal(bundle, state, narrowing.action);

	if (spentRefusal !== undefined) {
		return { rejection: spentRefusal.rejection, type: 'action_rejected' };
	}

	// Steps 2–3 (per-action validation and ordered effects) — the handlers.
	// The cursor stamps the turn the action BECOMES (current count + 1).
	const cursor = createTurnCursor(state.progression.turnCount + 1);
	const dispatched = dispatchAction(bundle, state, narrowing.action, cursor);

	if (dispatched.type === 'rejected') {
		return { rejection: dispatched.rejection, type: 'action_rejected' };
	}

	// Step 3b — the spend (D152): a completed act in the thing's `spentBy`
	// counts, and the crossing speaks and acts, before time moves.
	const spent = spendDurability(bundle, narrowing.action, dispatched);
	// D235: a body spoken to, or offered a thing, is who the player is talking with — its pronoun names it next input.
	const handled = { ...spent, state: rememberConversationPartner(spent.state, addressedActorOf(narrowing.action)) };

	// Steps 4–8 — time, the watchers, the agents, the commit (`complete-turn.ts`).
	return {
		result: completeTurn(bundle, state, handled, {
			commandInHand: commandInHandOf(command),
			detail: narrowing.action.type,
			family: 'command_executed',
		}),
		type: 'executed',
	};
};

/**
 * The actions that name nothing and take no shape — one handler signature,
 * one table. Typed against the action union so a new object-free action is a
 * compile error until it is routed.
 */
type ObjectFreeActionType = 'help' | 'inventory' | 'look' | 'map' | 'search_room' | 'wait';

const OBJECT_FREE_HANDLERS: Readonly<
	Record<ObjectFreeActionType, (bundle: EngineGameBundle, state: RuntimeState, cursor: TranscriptCursor) => ActionHandlerResult>
> = {
	help: handleHelp,
	inventory: handleInventory,
	look: handleLook,
	map: handleMap,
	search_room: handleSearchRoom,
	wait: handleWait,
};

/** Whether an action is one the object-free table routes. */
const isObjectFree = (action: RuntimeAction): action is Extract<RuntimeAction, { type: ObjectFreeActionType }> =>
	Object.hasOwn(OBJECT_FREE_HANDLERS, action.type);

/**
 * D235: whether an action passes a thing to a body — `give` and `show`, one
 * pair, one handler (the door and switch pairs' shape). A guard rather than
 * two switch arms, for the complexity cap's sake.
 */
const isGift = (action: RuntimeAction): action is Extract<RuntimeAction, { type: 'give' | 'show' }> => action.type === 'give' || action.type === 'show';

/** D240: whether an action is one of a body's two acts — `inspect_actor` and `search_actor`, one guard for the complexity cap's sake. */
const isBodyAct = (action: RuntimeAction): action is Extract<RuntimeAction, { type: 'inspect_actor' | 'search_actor' }> =>
	action.type === 'inspect_actor' || action.type === 'search_actor';

/** D251/D252: whether an action is answered by an authored table — a verb's arms (`act`) or a body's orders — one guard for the complexity cap's sake. */
const isAuthoredTable = (action: RuntimeAction): action is Extract<RuntimeAction, { type: 'act' | 'order' }> =>
	action.type === 'act' || action.type === 'order';

/** Route one narrowed action to its handler (the table, then `switch` + `never`, §3.5). */
const dispatchAction = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	action: RuntimeAction,
	cursor: TranscriptCursor,
): ActionHandlerResult => {
	if (isObjectFree(action)) {
		return OBJECT_FREE_HANDLERS[action.type](bundle, state, cursor);
	}

	if (isGift(action)) {
		return handleGive(bundle, state, action, cursor);
	}

	// D232, D240: looking at a body, and going through what it carries — the two acts a body answers besides speech and gifts.
	if (isBodyAct(action)) {
		return handleBodyAct(bundle, state, action, cursor);
	}

	// D196: an authored verb — the pack's own word, answered by the thing's own arms; D252: a body told to do something.
	if (isAuthoredTable(action)) {
		return handleAuthoredTable(bundle, state, action, cursor);
	}

	switch (action.type) {
		case 'ask': {
			return handleAsk(bundle, state, action, cursor);
		}
		// D234: the strike — the player's half of a fight; the foe's is the tick's.
		case 'attack': {
			return handleAttack(bundle, state, action, cursor);
		}
		// D50: both door verbs share one handler — they differ only in which
		// direction the state moves and which description speaks.
		case 'close_container':
		case 'open_container': {
			return handleDoor(bundle, state, action, cursor);
		}
		case 'drop': {
			return handleDrop(bundle, state, action, cursor);
		}
		// D143: the wear pair — one idea pointed two ways, like the doors.
		case 'equip':
		case 'unequip': {
			return handleWear(bundle, state, action, cursor);
		}
		case 'go': {
			return handleGo(bundle, state, action, cursor);
		}
		case 'inspect': {
			return handleInspect(bundle, state, action, cursor);
		}
		case 'inspect_set': {
			return handleInspectSet(bundle, state, action, cursor);
		}
		case 'search': {
			return handleSearch(bundle, state, action, cursor);
		}
		// D56: the switch pair, sharing one handler with the door pair for the
		// same reason those two share one — they are one idea pointed two ways.
		case 'switch_off':
		case 'switch_on': {
			return handleSwitch(bundle, state, action, cursor);
		}
		case 'take': {
			return handleTake(bundle, state, action, cursor);
		}
		case 'use': {
			return handleUse(bundle, state, action, cursor);
		}
	}
};

/**
 * R29: the first item this action would ACT on across an unmet barrier, if
 * any. Observation (`inspect`, `ask`, destination-only `go`) and things the
 * player already holds are exempt; everything else that names an item crosses
 * the barrier to do its work.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the turn
 * @param action - The narrowed action
 * @returns The barred item, or `undefined` when the way is clear
 */
const findUnreachableActedItem = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	action: RuntimeAction,
): ItemId | undefined => actedOnItemIdsOf(action).find((itemId) => isItemUnreachable(bundle, state, itemId));

/**
 * Whether an action puts HANDS on the items it names (R29's reach gate reads
 * this) or merely refers to them — the observation verbs, movement, and the
 * carry family (`drop`, and D143's wear pair, act on what is already in
 * hand) reference without reaching. A table rather than a switch so that
 * adding an action is a compile error until its reach is declared.
 */
const ACTION_REACHES: Readonly<Record<RuntimeAction['type'], boolean>> = {
	// D196: an authored verb puts hands on its thing — the author who wants an
	// act at a distance gates it with a condition, not with the barrier.
	act: true,
	ask: false,
	// D234: the weapon named after `with` must come to hand — a carried one already has.
	attack: true,
	close_container: true,
	drop: false,
	equip: false,
	// D235: the thing offered must be in the hands (the handler's first rung) — a thing behind glass is refused at the barrier first.
	give: true,
	go: false,
	help: false,
	inspect: false,
	// D232: a look at a body reaches nothing; reach was the intercept's to judge, and the look is observation.
	inspect_actor: false,
	inspect_set: false,
	inventory: false,
	look: false,
	map: false,
	open_container: true,
	// D252: an order is speech — the body acts, if it does, by its own arms; the player's hands reach nothing.
	order: false,
	search: true,
	// D240: a body's reach was the intercept's to judge, as a look's is; its pockets are reached with it.
	search_actor: false,
	search_room: false,
	show: true,
	switch_off: true,
	switch_on: true,
	take: true,
	unequip: false,
	// `use` reaches BOTH: the instrument must come to hand (a carried one
	// already has — the reachability walk says so) and the target must be
	// touchable for the interaction to land.
	use: true,
	wait: false,
};

/**
 * Which item ids one action must REACH (as distinct from merely reference —
 * the observation verbs reference without reaching).
 *
 * @param action - The narrowed action
 * @returns The item ids the action would act on
 */
const actedOnItemIdsOf = (action: RuntimeAction): readonly ItemId[] =>
	ACTION_REACHES[action.type] ? itemIdsReferencedBy(action) : [];

